import { Button } from '@base-ui/react/button';
import { Input } from '@base-ui/react/input';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import styled from 'styled-components';
import api, { queryClient } from '@/api';
import type { components } from '@/api/qraft/schema';
import { DraftAncestorNotice } from '~/components/ui/DraftAncestorNotice';
import { Field, FieldLabel, FieldValue } from '~/components/ui/FieldDisplay';
import { BackLink, ForwardLink } from '~/components/ui/NavLinks';
import { PageTitle } from '~/components/ui/Page';
import { FieldGrid, Section, SectionTitle } from '~/components/ui/Section';
import buttonStyles from '~/styles/button.module.css';

type LessonAdmin = components['schemas']['LessonAdmin'];

export const Route = createFileRoute('/_admin/lessons/$lessonId')({
  component: LessonDetail,
});

function LessonDetail() {
  const { lessonId } = Route.useParams();
  const lessonIdNum = Number(lessonId);
  const [editing, setEditing] = useState(false);

  // Admin-only read: the student lesson_detail is gated to a fully published
  // path, so a draft lesson would 404 there.
  const admin = api.adminGetLesson.useQuery({
    path: { lesson_id: lessonIdNum },
  });

  if (admin.isLoading) return <Message>Loading lesson...</Message>;
  if (admin.error || !admin.data)
    return <Message $error>Error loading lesson</Message>;

  const lesson = admin.data;

  return (
    <Container>
      <BackLink>
        <Link
          to="/lessons"
          search={{ course: lesson.course_id, version: lesson.course_version }}
        >
          &larr; Back to lessons
        </Link>
      </BackLink>

      <Header>
        <PageTitle>{lesson.title}</PageTitle>
        {!editing && (
          <Button
            className={buttonStyles.Button}
            onClick={() => setEditing(true)}
          >
            Edit
          </Button>
        )}
      </Header>

      {editing ? (
        <LessonEditor
          key={lesson.id}
          lesson={lesson}
          onDone={() => setEditing(false)}
        />
      ) : (
        <>
          <Section>
            <SectionTitle>Overview</SectionTitle>
            <FieldGrid>
              <Field>
                <FieldLabel>ID</FieldLabel>
                <FieldValue>{lesson.id}</FieldValue>
              </Field>
              <Field>
                <FieldLabel>Slug</FieldLabel>
                <FieldValue>{lesson.slug || '—'}</FieldValue>
              </Field>
              <Field>
                <FieldLabel>Order</FieldLabel>
                <FieldValue>{lesson.order}</FieldValue>
              </Field>
              <Field>
                <FieldLabel>Status</FieldLabel>
                <FieldValue>{lesson.status}</FieldValue>
              </Field>
              <Field>
                <FieldLabel>Passing score</FieldLabel>
                <FieldValue>{lesson.passing_score}</FieldValue>
              </Field>
              <Field>
                <FieldLabel>Auto-grade</FieldLabel>
                <FieldValue>{lesson.auto_grade ? 'Yes' : 'No'}</FieldValue>
              </Field>
              <Field>
                <FieldLabel>Course</FieldLabel>
                <FieldValue>
                  <Link
                    to="/courses/$courseId/$version"
                    params={{
                      courseId: String(lesson.course_id),
                      version: String(lesson.course_version),
                    }}
                  >
                    #{lesson.course_id} v{lesson.course_version}
                  </Link>
                </FieldValue>
              </Field>
              <Field>
                <FieldLabel>Topics</FieldLabel>
                <FieldValue>{lesson.topics_count}</FieldValue>
              </Field>
            </FieldGrid>
            {lesson.thumbnail && (
              <ThumbField>
                <FieldLabel as="div">Thumbnail</FieldLabel>
                <ThumbFrame>
                  <img
                    src={lesson.thumbnail}
                    alt={`${lesson.title} thumbnail`}
                  />
                </ThumbFrame>
              </ThumbField>
            )}
          </Section>

          <ForwardLinks>
            <ForwardLink>
              <Link to="/topics" search={{ lesson: lessonIdNum }}>
                View topics &rarr;
              </Link>
            </ForwardLink>
            <ForwardLink>
              <Link to="/questions" search={{ lesson: lessonIdNum }}>
                View questions &rarr;
              </Link>
            </ForwardLink>
          </ForwardLinks>
        </>
      )}
    </Container>
  );
}

function LessonEditor({
  lesson,
  onDone,
}: {
  lesson: LessonAdmin;
  onDone: () => void;
}) {
  const navigate = useNavigate();
  const [title, setTitle] = useState(lesson.title);
  const [slug, setSlug] = useState(lesson.slug);
  const [order, setOrder] = useState(String(lesson.order));
  const [status, setStatus] = useState(lesson.status);
  const [passingScore, setPassingScore] = useState(
    String(lesson.passing_score),
  );
  const [autoGrade, setAutoGrade] = useState(lesson.auto_grade);
  const [thumbnailKey, setThumbnailKey] = useState(lesson.thumbnail_key);
  const [thumbnailUrl, setThumbnailUrl] = useState(lesson.thumbnail);

  const invalidate = () => {
    queryClient.invalidateQueries({
      queryKey: api.adminGetLesson.getQueryKey({
        path: { lesson_id: lesson.id },
      }),
    });
    queryClient.invalidateQueries({
      queryKey: api.adminListLessons.getQueryKey({
        path: { course_id: lesson.course_id },
      }),
    });
  };

  const uploadMutation = api.adminUploadMedia.useMutation(undefined, {
    onSuccess: (res) => {
      setThumbnailKey(res.key);
      setThumbnailUrl(res.url);
    },
  });

  const updateMutation = api.adminUpdateLesson.useMutation(undefined, {
    onSuccess: () => {
      invalidate();
      onDone();
    },
  });

  const deleteMutation = api.adminDeleteLesson.useMutation(undefined, {
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: api.adminListLessons.getQueryKey({
          path: { course_id: lesson.course_id },
        }),
      });
      navigate({
        to: '/lessons',
        search: { course: lesson.course_id, version: lesson.course_version },
      });
    },
  });

  const trimmedTitle = title.trim();

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) uploadMutation.mutate({ body: { file } });
  };

  const handleSave = () => {
    // Block save while a thumbnail is still uploading, or the old key persists
    // and the just-picked image is silently dropped.
    if (!trimmedTitle || updateMutation.isPending || uploadMutation.isPending)
      return;
    updateMutation.mutate({
      path: { lesson_id: lesson.id },
      body: {
        title: trimmedTitle,
        slug,
        order: Number(order),
        status,
        passing_score: Number(passingScore),
        auto_grade: autoGrade,
        thumbnail_key: thumbnailKey,
      },
    });
  };

  const handleDelete = () => {
    if (!window.confirm('Delete this lesson? This cannot be undone.')) return;
    deleteMutation.mutate({ path: { lesson_id: lesson.id } });
  };

  return (
    <Section>
      <SectionTitle>Edit lesson</SectionTitle>
      <FormGrid>
        <FormRow>
          <FormLabel htmlFor="lesson-title">Title</FormLabel>
          <TextInput
            id="lesson-title"
            value={title}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setTitle(e.target.value)
            }
          />
        </FormRow>
        <FormRow>
          <FormLabel htmlFor="lesson-slug">Slug</FormLabel>
          <TextInput
            id="lesson-slug"
            value={slug}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setSlug(e.target.value)
            }
          />
        </FormRow>
        <FormRow>
          <FormLabel htmlFor="lesson-order">Order</FormLabel>
          <TextInput
            id="lesson-order"
            type="number"
            value={order}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setOrder(e.target.value)
            }
          />
        </FormRow>
        <FormRow>
          <FormLabel htmlFor="lesson-passing">Passing score</FormLabel>
          <TextInput
            id="lesson-passing"
            type="number"
            value={passingScore}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setPassingScore(e.target.value)
            }
          />
        </FormRow>
        <FormRow>
          <FormLabel htmlFor="lesson-status">Status</FormLabel>
          <StatusSelect
            id="lesson-status"
            value={status}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
              setStatus(e.target.value as LessonAdmin['status'])
            }
          >
            <option value="draft">draft</option>
            <option value="published">published</option>
          </StatusSelect>
          <DraftAncestorNotice
            entityLabel="lesson"
            ancestors={[
              { label: 'course', status: lesson.course_status },
              { label: 'series', status: lesson.series_status },
            ]}
          />
        </FormRow>
        <CheckboxRow>
          <input
            id="lesson-autograde"
            type="checkbox"
            checked={autoGrade}
            onChange={(e) => setAutoGrade(e.target.checked)}
          />
          <FormLabel htmlFor="lesson-autograde">Auto-grade</FormLabel>
        </CheckboxRow>
        <FormRow>
          <FormLabel htmlFor="lesson-thumbnail">Thumbnail</FormLabel>
          {thumbnailUrl && (
            <ThumbFrame>
              <img src={thumbnailUrl} alt="Lesson thumbnail" />
            </ThumbFrame>
          )}
          <input
            id="lesson-thumbnail"
            type="file"
            accept="image/*"
            onChange={handleFile}
          />
          {uploadMutation.isPending && <Hint>Uploading...</Hint>}
          {uploadMutation.isError && (
            <Message $error>Upload failed — must be a valid image.</Message>
          )}
        </FormRow>
      </FormGrid>

      {updateMutation.isError && (
        <Message $error>Could not save changes. Please try again.</Message>
      )}
      {deleteMutation.isError && (
        <Message $error>
          Could not delete — the lesson may still have topics.
        </Message>
      )}

      <Actions>
        <Button
          className={buttonStyles.Button}
          onClick={handleSave}
          disabled={
            updateMutation.isPending ||
            uploadMutation.isPending ||
            !trimmedTitle
          }
        >
          {updateMutation.isPending ? 'Saving...' : 'Save'}
        </Button>
        <Button className={buttonStyles.Button} onClick={onDone}>
          Cancel
        </Button>
        <DeleteButton
          className={buttonStyles.Button}
          onClick={handleDelete}
          disabled={deleteMutation.isPending}
        >
          {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
        </DeleteButton>
      </Actions>
    </Section>
  );
}

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--component-margin);
`;

const FormGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: var(--inner-padding);
`;

const FormRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
`;

const CheckboxRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const FormLabel = styled.label`
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--grey-700);
`;

const fieldStyles = `
  box-sizing: border-box;
  width: 100%;
  padding: 0.5rem 0.75rem;
  border: 1px solid var(--grey-300);
  border-radius: 0.375rem;
  font-size: 0.875rem;
  background-color: var(--grey-50);
  color: var(--grey-900);

  &:focus {
    outline: 2px solid var(--blue);
    outline-offset: -1px;
  }
`;

const TextInput = styled(Input)`
  ${fieldStyles}
`;

const StatusSelect = styled.select`
  ${fieldStyles}
`;

const ThumbField = styled.div`
  padding: 0 var(--inner-padding) var(--inner-padding);
`;

// Fixed 16:9 + object-fit cover so varied thumbnails look uniform.
const ThumbFrame = styled.div`
  display: inline-block;
  margin-top: 0.375rem;
  padding: 0.375rem;
  background-color: var(--grey-75);
  border: 1px solid var(--grey-200);
  border-radius: 8px;
  box-shadow: var(--shadow-md);

  img {
    display: block;
    width: 240px;
    height: 135px;
    object-fit: cover;
    border-radius: 5px;
    background-color: var(--grey-100);
  }
`;

const Hint = styled.div`
  font-size: 0.8125rem;
  color: var(--grey-600);
`;

const Actions = styled.div`
  display: flex;
  gap: 0.75rem;
  padding: var(--inner-padding);
`;

const DeleteButton = styled(Button)`
  margin-left: auto;
  color: var(--error);
`;

const ForwardLinks = styled.div`
  display: flex;
  gap: 1.5rem;
`;

const Container = styled.div``;

const Message = styled.div<{ $error?: boolean }>`
  padding: 2rem;
  text-align: center;
  color: ${(p) => (p.$error ? 'var(--error)' : 'var(--grey-600)')};
`;
